import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sendEmailVerification } from 'firebase/auth';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import {
  previewClientInvite,
  acceptClientInviteByToken,
  declineClientInvite,
} from '../../api/clientInviteApi';
import {
  getClientInviteToken,
  setClientInviteToken,
  clearClientInviteToken,
} from '../../utils/clientInviteToken';
import {
  ADVISOR_CONSENT_DOC_PATH,
  LEGAL_DRAFT_MARKER,
} from '../../config/advisorConsentAccess';
import { useLegalDocument } from './useLegalDocument';
import AdvisorConsentDialog from './AdvisorConsentDialog';

/**
 * WL-K5a — Annahme-Sicht für Kanzlei→Mandant-Reverse-Einladungen (Mandanten-Seite).
 *
 * Liest den Token aus `?clientInvite=<token>` (Mail-Link) mit localStorage-Fallback
 * (`pendingClientInviteToken`). BEWUSST ein EIGENER Token-Key, NICHT der M11b-
 * Advisor-Key — sonst würde `useIsAdvisorContext` den frischen Mandanten als
 * Steuerberater fehlklassifizieren (B-E5).
 *
 * Ablauf: Preview (Kanzlei-Name, read-only) → Verifikations-Gate (E-Mail bestätigt)
 * → expliziter Accept ODER Decline → Erfolgs-/Ablehnungs-Latch → `reloadTenants()`.
 * Der Mandant bleibt Owner seines eigenen Tenants; die Kanzlei erhält lesenden
 * `advisor`-Zugriff — es findet KEIN Tenant-Wechsel auf der Mandantenseite statt.
 *
 * Token-Hardening (F6): der Token wird beim Mount aus der URL in localStorage
 * gesichert und via `history.replaceState` aus der sichtbaren URL entfernt.
 *
 * Rendert nichts, wenn kein Token vorliegt oder kein User eingeloggt ist (self-
 * gating; kein Flag — die harte Sperre erzwingt der Server über die Beta der
 * einladenden Kanzlei).
 */
const ClientInviteAcceptPanel: React.FC = () => {
  const { t } = useTranslation('common');
  const { currentUser, reloadTenants, refreshPendingClientInvites } = useAuth();
  const [searchParams] = useSearchParams();

  const urlToken = (searchParams.get('clientInvite') || '').trim();
  const token = (urlToken || getClientInviteToken() || '').trim();

  const [working, setWorking] = useState(false);
  const [verified, setVerified] = useState<boolean>(!!currentUser?.emailVerified);
  const [result, setResult] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [kanzleiName, setKanzleiName] = useState<string | null>(null);
  // Latch: hält die Erfolgs-/Ablehnungs-Sektion sichtbar, auch nachdem der Token
  // entfernt wurde (sonst würde die Komponente sofort wieder null rendern).
  const [terminal, setTerminal] = useState<'accepted' | 'declined' | null>(null);
  // §3-Consent (B3): Dialog nur dazwischenschalten, wenn die §3-Datei freigegeben
  // (`ready`) ist. Fehlt/Draft ⇒ Bestandsverhalten (Accept ohne consentAccepted).
  const [showConsent, setShowConsent] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const consentDoc = useLegalDocument(ADVISOR_CONSENT_DOC_PATH, LEGAL_DRAFT_MARKER);

  const active = !!token && !!currentUser;

  // Token-Hardening (F6): aus URL in localStorage sichern + Token aus URL strippen.
  useEffect(() => {
    if (!urlToken) return;
    setClientInviteToken(urlToken);
    refreshPendingClientInvites();
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('clientInvite');
      window.history.replaceState({}, '', url.toString());
    } catch {
      // best-effort — reine URL-Kosmetik.
    }
  }, [urlToken, refreshPendingClientInvites]);

  // Kanzlei-Name vorab laden (read-only). Jeder Fehler → null (generischer Text).
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    (async () => {
      try {
        const { kanzleiName: name } = await previewClientInvite(token);
        if (!cancelled) setKanzleiName(name);
      } catch (err) {
        logger.error('[ClientInviteAcceptPanel] preview failed', err);
        if (!cancelled) setKanzleiName(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, token]);

  // Inert — außer ein Terminal-Latch hält das Panel sichtbar.
  if (!terminal && (!active || !currentUser)) {
    return null;
  }

  const handleResendVerification = async () => {
    if (!currentUser) return;
    setResult(null);
    setWorking(true);
    try {
      await sendEmailVerification(currentUser);
      setResult({ type: 'info', message: t('clientInvite.acceptVerificationSent') });
    } catch (err) {
      logger.error('[ClientInviteAcceptPanel] resend verification failed', err);
      setResult({ type: 'error', message: t('clientInvite.acceptError') });
    } finally {
      setWorking(false);
    }
  };

  const handleRecheck = async () => {
    if (!currentUser) return;
    setResult(null);
    setWorking(true);
    try {
      await currentUser.reload();
      const nowVerified = !!currentUser.emailVerified;
      setVerified(nowVerified);
      if (!nowVerified) {
        setResult({ type: 'info', message: t('clientInvite.acceptStillUnverified') });
      }
    } catch (err) {
      logger.error('[ClientInviteAcceptPanel] reload failed', err);
      setResult({ type: 'error', message: t('clientInvite.acceptError') });
    } finally {
      setWorking(false);
    }
  };

  // Klick auf „Annehmen": §3-Consent-Dialog nur bei freigegebener Datei erzwingen.
  const handleAcceptClick = () => {
    if (consentDoc.kind === 'ready') {
      setConsentError(null);
      setShowConsent(true);
      return;
    }
    void doAccept(false);
  };

  const doAccept = async (viaConsent: boolean) => {
    setResult(null);
    setWorking(true);
    try {
      // §3-Dialog-Pfad sendet consentAccepted:true; inerter Direkt-Pfad hält die
      // Bestandsarität (1 Arg) für die bestehenden Spec-Tests.
      const { kanzleiName: resolvedName } = viaConsent
        ? await acceptClientInviteByToken(token, true)
        : await acceptClientInviteByToken(token);
      clearClientInviteToken();
      setShowConsent(false);
      setConsentError(null);
      refreshPendingClientInvites();
      // Tenant-Listen neu laden (die Kanzlei ist nun advisor-Member des eigenen
      // Tenants). Der Mandant bleibt Owner — KEIN switchTenant.
      await reloadTenants();
      const displayName = resolvedName ?? kanzleiName;
      setKanzleiName(displayName);
      setResult({
        type: 'success',
        message: displayName
          ? t('clientInvite.acceptSuccessKanzlei', { kanzlei: displayName })
          : t('clientInvite.acceptSuccess'),
      });
      setTerminal('accepted');
    } catch (err) {
      // Dialog schließen, damit die Panel-Meldung sichtbar wird.
      setShowConsent(false);
      const code = (err as { code?: string })?.code;
      if (code === 'permission-denied') {
        setResult({ type: 'error', message: t('clientInvite.acceptEmailMismatch') });
      } else if (code === 'failed-precondition') {
        setVerified(false);
        setResult({ type: 'info', message: t('clientInvite.acceptVerifyEmailNotice') });
      } else {
        logger.error('[ClientInviteAcceptPanel] accept failed', err);
        setResult({ type: 'error', message: t('clientInvite.acceptError') });
      }
    } finally {
      setWorking(false);
    }
  };

  const handleDecline = async () => {
    setResult(null);
    setWorking(true);
    try {
      await declineClientInvite(token);
      clearClientInviteToken();
      refreshPendingClientInvites();
      setResult({ type: 'info', message: t('clientInvite.declineDone') });
      setTerminal('declined');
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (code === 'permission-denied') {
        setResult({ type: 'error', message: t('clientInvite.acceptEmailMismatch') });
      } else {
        logger.error('[ClientInviteAcceptPanel] decline failed', err);
        setResult({ type: 'error', message: t('clientInvite.acceptError') });
      }
    } finally {
      setWorking(false);
    }
  };

  return (
    <section className="mb-6 rounded-lg bg-surface-raised ring-1 ring-indigo-500/30 p-4">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-accent-ink mb-2">
        {t('clientInvite.acceptTitle')}
      </h2>

      {!terminal && kanzleiName && (
        <p className="text-sm text-[var(--tone-accent-fg)] leading-relaxed mb-2">
          {t('clientInvite.acceptFromKanzlei', { kanzlei: kanzleiName })}
        </p>
      )}

      {terminal ? (
        <p className={`text-sm ${terminal === 'accepted' ? 'text-[var(--tone-success-fg)]' : 'text-secondary'}`}>
          {result?.message}
        </p>
      ) : !verified ? (
        <div>
          <p className="text-sm text-secondary leading-relaxed">{t('clientInvite.acceptVerifyEmailNotice')}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={working}
              className="inline-flex items-center rounded-md bg-surface-inset px-3 py-1.5 text-xs font-medium text-secondary hover:bg-surface-hover disabled:opacity-50"
            >
              {t('clientInvite.acceptResendVerification')}
            </button>
            <button
              type="button"
              onClick={handleRecheck}
              disabled={working}
              className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {working ? t('clientInvite.working') : t('clientInvite.acceptRecheck')}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <p className="text-sm text-secondary leading-relaxed">{t('clientInvite.acceptReadyNotice')}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleAcceptClick}
              disabled={working}
              className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {working ? t('clientInvite.working') : t('clientInvite.acceptButton')}
            </button>
            <button
              type="button"
              onClick={handleDecline}
              disabled={working}
              className="inline-flex items-center rounded-md bg-surface-inset px-3 py-1.5 text-sm font-medium text-secondary hover:bg-surface-hover disabled:opacity-50"
            >
              {t('clientInvite.declineButton')}
            </button>
          </div>
        </div>
      )}

      {result && !terminal && (
        <p className={`mt-2 text-xs ${result.type === 'error' ? 'text-[var(--tone-danger-fg)]' : 'text-muted'}`}>
          {result.message}
        </p>
      )}

      {/* §3-Mandanten-Consent-Dialog (B3): kanzlei_name := previewClientInvite-Name.
          Nur sichtbar, wenn die §3-Datei freigegeben ist. */}
      {showConsent && (
        <AdvisorConsentDialog
          kanzleiName={kanzleiName}
          submitting={working}
          error={consentError}
          onGrant={() => doAccept(true)}
          onCancel={() => {
            setShowConsent(false);
            setConsentError(null);
          }}
        />
      )}
    </section>
  );
};

export default ClientInviteAcceptPanel;
